import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useState } from 'react';
import { Check, Link, Mail, MessageCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/shared/widgets/sheet';
import type { ListingDesignItem } from '../../domain/models/ProductListing';
import { normalizeProductPageUrl } from '@/features/pdp/utils/pdpUrl';

export function ProductShareSheet({ product, onClose }: { product: ListingDesignItem; onClose: () => void }) {
  const { analytics } = useAnalytics();
  const trackShare = (channel: 'Mail' | 'WhatsApp' | 'Copy Link') => analytics?.trackEvent('Share Product', {
    'Product Id': String(product.designId),
    'Share Channel': channel,
  });
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const pdpUrl = normalizeProductPageUrl(product.productPageUrl);
  const url = pdpUrl ? new URL(pdpUrl, window.location.origin).href : '';
  const message = `${product.designName}\n${url}`;
  const rowClass = 'flex w-full items-center gap-4 rounded-2xl border border-[#D5E8FB] bg-[#F3F9FF] p-4 text-left text-sm';
  const iconClass = 'h-12 w-12 shrink-0 rounded-2xl bg-[#E2F2FF] p-3 text-[#222222]';
  return <Sheet open onOpenChange={open => { if (!open) onClose(); }}>
    <SheetContent side="bottom" aria-describedby={undefined} onCloseAutoFocus={event => event.preventDefault()} className="left-0 right-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-3xl p-5">
      <SheetTitle>Share</SheetTitle>
      <p className="mb-5 mt-1 text-sm text-[#8B7566]">Let someone know what you like</p>
      {url ? <div className="space-y-3">
        <a className={rowClass} onClick={() => trackShare('Mail')} href={`mailto:?subject=${encodeURIComponent(product.designName)}&body=${encodeURIComponent(message)}`}><Mail className={iconClass} /><span>Share via Email</span></a>
        <a className={rowClass} onClick={() => trackShare('WhatsApp')} href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer"><MessageCircle className={iconClass} /><span>Share via WhatsApp</span></a>
        <button type="button" className={rowClass} onClick={async () => {
          trackShare('Copy Link');
          setError('');
          try { await navigator.clipboard.writeText(url); setCopied(true); }
          catch { setCopied(false); setError('Could not copy automatically. Select and copy the link below.'); }
        }}>{copied ? <Check className={iconClass} /> : <Link className={iconClass} />}<span>{copied ? 'Link copied' : 'Copy Link'}</span></button>
        <p role="status" className="sr-only">{copied ? 'Link copied' : ''}</p>
        {error && <><p role="alert" className="text-sm text-red-600">{error}</p><input aria-label="Product link" readOnly value={url} onFocus={event => event.currentTarget.select()} className="w-full rounded border p-2 text-xs" /></>}
      </div> : <p role="alert">Product link is unavailable.</p>}
    </SheetContent>
  </Sheet>;
}
